import { Access, AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import {
  PATH_ADD,
  PATH_ANALYTICS,
  PATH_APPROVALS,
  PATH_ASSETS,
  PATH_CATEGORIES,
  PATH_COUNTERS,
  PATH_CREATE_VERSION,
  PATH_DASHBOARD,
  PATH_DATA_IMPORT,
  PATH_DOCUMENTATION,
  PATH_EDIT,
  PATH_FORM_CHECKLISTS,
  PATH_FORM_TEMPLATES,
  PATH_FORM_TEMPLATES_ACTIVE,
  PATH_FORM_TEMPLATES_ARCHIVED,
  PATH_IMPORT,
  PATH_INTERVENTIONS,
  PATH_PERFORM_TASKS,
  PATH_SECTORS,
  PATH_SITES,
  PATH_TASKS,
  PATH_TEAMS,
  PATH_USERS,
  PATH_HOME,
  PATH_WIDGETS,
  PATH_CUSTOMIZE,
} from '@lib/enums/urls';

function matchPath(pathTemplate: string, actualPath: string): boolean {
  const normalizedPathname = actualPath.startsWith('/')
    ? actualPath.slice(1)
    : actualPath;
  const regex = new RegExp(`^${pathTemplate.replace(/:[^/]+/g, '[^/]+')}$`);
  return (
    normalizedPathname.startsWith(pathTemplate) ||
    regex.test(normalizedPathname)
  );
}

// Components every company plan payload must enumerate — the system app's
// plan form writes exactly this set (see apps/system Company form /
// getPreparedDataFromValues). A loaded accesses list that lacks one of these
// records is broken BE data, so access checks fail closed (LimitedRead)
// instead of reading absence as "not restricted".
const PLAN_GATED_COMPONENTS: AccessComponentEnum[] = [
  AccessComponentEnum.DocumentationManagement,
  AccessComponentEnum.InterventionsManagement,
];

const ACCESSES_MAP = [
  {
    path: `${PATH_HOME}/${PATH_CUSTOMIZE}`,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: `${PATH_HOME}/${PATH_WIDGETS}`,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: `${PATH_HOME}/${PATH_WIDGETS}/${PATH_ADD}`,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: PATH_HOME,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: `${PATH_INTERVENTIONS}/${PATH_CATEGORIES}`,
    component: AccessComponentEnum.InterventionCategoriesManagement,
  },
  {
    path: PATH_INTERVENTIONS,
    component: AccessComponentEnum.InterventionsManagement,
  },
  {
    path: `${PATH_TASKS}/:ID/${PATH_INTERVENTIONS}`,
    component: AccessComponentEnum.InterventionsManagement,
  },
  {
    path: `${PATH_ASSETS}/:ID/${PATH_INTERVENTIONS}`,
    component: AccessComponentEnum.InterventionsManagement,
  },
  {
    path: `${PATH_DOCUMENTATION}/${PATH_CATEGORIES}`,
    component: AccessComponentEnum.DocumentationCategoriesManagement,
  },
  {
    path: `${PATH_DOCUMENTATION}/${PATH_ANALYTICS}`,
    component: AccessComponentEnum.DocumentationAnalytics,
  },
  {
    path: `${PATH_DOCUMENTATION}/${PATH_APPROVALS}`,
    component: AccessComponentEnum.DocumentationManagement,
  },
  {
    path: PATH_DOCUMENTATION,
    component: AccessComponentEnum.DocumentationManagement,
  },
  {
    path: PATH_PERFORM_TASKS,
    component: AccessComponentEnum.TasksPerforming,
  },
  {
    path: PATH_TASKS,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: `${PATH_FORM_TEMPLATES}/${PATH_CATEGORIES}`,
    component: AccessComponentEnum.FormCategoriesManagement,
  },
  {
    path: PATH_FORM_TEMPLATES,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_TEMPLATES_ACTIVE,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_TEMPLATES_ARCHIVED,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_CHECKLISTS,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: `${PATH_SITES}/:ID/${PATH_USERS}`,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: PATH_SITES,
    component: AccessComponentEnum.SitesManagement,
  },
  {
    path: `${PATH_SECTORS}/:ID/${PATH_USERS}`,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: PATH_SECTORS,
    component: AccessComponentEnum.SitesManagement,
  },
  {
    path: PATH_USERS,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: PATH_TEAMS,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: `${PATH_ASSETS}/${PATH_CATEGORIES}`,
    component: AccessComponentEnum.AssetCategoriesManagement,
  },
  {
    path: `${PATH_ASSETS}/${PATH_COUNTERS}`,
    component: AccessComponentEnum.AssetsCounterReading,
  },
  {
    path: PATH_ASSETS,
    component: AccessComponentEnum.AssetsManagement,
  },
  {
    path: PATH_DASHBOARD,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_TEMPLATES,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_DATA_IMPORT,
    component: [
      AccessComponentEnum.SitesManagement,
      AccessComponentEnum.AssetsManagement,
    ],
  },
];

// Intended for a user's finalAccesses, where an absent record stays
// `undefined` — otherwise plan-locked users get bounced to access-denied
// instead of reaching the page-level FeatureLockState upgrade CTA. For
// company plan.accesses payloads use checkCompanyPlanAccessByComponent,
// which fails closed on PLAN_GATED_COMPONENTS; plan gating is feature-level
// and must not depend on the pathname→component map (sub-pages like
// /interventions/categories map to components plans never enumerate).
export function checkUserAccessForPage(
  accesses: Access[] | undefined,
  pathname: string,
) {
  if (!pathname || !accesses?.length) return undefined;

  const matchedRoute = ACCESSES_MAP.find(({ path }) =>
    matchPath(path, pathname),
  );

  if (!matchedRoute) return undefined;

  const { component } = matchedRoute;

  if (Array.isArray(component)) {
    const relevantAccesses = accesses.filter(({ component: userComponent }) =>
      component.includes(userComponent),
    );

    if (!relevantAccesses.length) return undefined;

    // LimitedRead < Read < Write
    const accessLevelsOrder = [
      AccessLevelEnum.LimitedRead,
      AccessLevelEnum.Read,
      AccessLevelEnum.Write,
    ];

    const maxLevel = relevantAccesses.reduce((max, curr) => {
      const currLevel = curr.level ?? AccessLevelEnum.LimitedRead;
      return accessLevelsOrder.indexOf(currLevel) >
        accessLevelsOrder.indexOf(max)
        ? currLevel
        : max;
    }, AccessLevelEnum.LimitedRead);

    const isEditingOrAdding = [
      PATH_ADD,
      PATH_EDIT,
      PATH_CREATE_VERSION,
      PATH_IMPORT,
    ].some((subPath) => pathname.includes(`/${subPath}`));

    if (isEditingOrAdding && maxLevel === AccessLevelEnum.Read) {
      return AccessLevelEnum.LimitedRead;
    }

    return maxLevel;
  }

  const access = accesses.find(
    ({ component: userComponent }) => userComponent === component,
  );

  if (!access) return undefined;

  const { level } = access;

  // Treat write-action sub-paths as edit-equivalent: Read → LimitedRead
  const isEditingOrAdding = [
    PATH_ADD,
    PATH_EDIT,
    PATH_CREATE_VERSION,
    PATH_IMPORT,
  ].some((subPath) => pathname.includes(`/${subPath}`));

  if (isEditingOrAdding && level === AccessLevelEnum.Read) {
    return AccessLevelEnum.LimitedRead;
  }

  return level || AccessLevelEnum.LimitedRead;
}

export function checkUserAccessByComponent(
  accesses: Access[] | undefined,
  findBy: AccessComponentEnum,
) {
  if (!findBy || !accesses?.length) return undefined;
  const access = accesses.find(({ component }) => component === findBy);
  if (access?.level) return access.level;
  return AccessLevelEnum.LimitedRead;
}

// Plan accesses only enumerate features the plan explicitly gates. An
// absent component means "this plan does not restrict the feature", which
// is distinct from `checkUserAccessByComponent`'s LimitedRead fallback
// (the latter is meant for the user's finalAccesses, where absence
// implies the user has no access). The exception is PLAN_GATED_COMPONENTS:
// every saved plan carries those records, so a loaded payload missing one
// (or missing its level) fails closed rather than granting access.
export function checkCompanyPlanAccessByComponent(
  accesses: Access[] | undefined,
  findBy: AccessComponentEnum,
) {
  if (!findBy || !accesses?.length) return undefined;
  const access = accesses.find(({ component }) => component === findBy);
  if (access?.level) return access.level;
  if (PLAN_GATED_COMPONENTS.includes(findBy)) {
    return AccessLevelEnum.LimitedRead;
  }
  return undefined;
}

export function checkUserAccessItemIdsByComponent(
  accesses: Access[] | undefined,
  findBy: AccessComponentEnum,
) {
  if (!findBy || !accesses?.length) return undefined;
  const access = accesses.find(({ component }) => component === findBy);
  return access?.itemIds;
}
